/**
 * Storage for Flagsmith's flag cache (`cacheFlags`).
 *
 * The SDK's default adapter rejects on a failed `localStorage.setItem` and the
 * SDK never awaits that write, so a full origin turns every flag refresh into
 * an unhandled `QuotaExceededError`. The cache is only a warm start; losing a
 * write is the right degradation.
 */
import { getSafeLocalStorage } from '@/lib/jotai-storage';
import { logger } from '@/utils/logger/logger';

type FlagsmithAsyncStorage = {
  getItemSync: (key: string) => string | null;
  getItem: (
    key: string,
    callback?: (error: Error | null, result: string | null) => void
  ) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<string>;
};

const reportedOperations = new Set<string>();

const errorNameOf = (error: unknown): string => {
  const name = (error as { name?: unknown } | null)?.name;
  return typeof name === 'string' ? name : typeof error;
};

export const createFlagsmithStorage = (): FlagsmithAsyncStorage => {
  const storage = getSafeLocalStorage((operation, _key, error) => {
    if (reportedOperations.has(operation)) return;
    reportedOperations.add(operation);
    logger.warn(`[flags] Flagsmith cache ${operation} failed`, {
      errorName: errorNameOf(error),
    });
  });

  return {
    getItemSync: (key) => storage.getItem(key),
    getItem: (key, callback) => {
      const result = storage.getItem(key);
      callback?.(null, result);
      return Promise.resolve(result);
    },
    setItem: (key, value) => {
      storage.setItem(key, value);
      return Promise.resolve(value);
    },
  };
};
